import { JsonLd } from "react-schemaorg"
import type { ItemList } from "schema-dts"

import { getCurrencySymbol, useFormatDecimals, useLocale, useTranslation } from "@bounce/i18n"

import { getAggregateRatingSpecification, getGeoCoordinatesSpecification } from "./StructuredData"
import { getStoreAnchor } from "../../LuggageStorage/StoreSlider/helpers"
import { getStoreThumbnail } from "@/libs/bounce/stores/getStoreThumbnail"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import { getStoreUrlPath, useUrl } from "@/libs/router"

type StoreItemListStructuredDataProps = {
  stores: NearbyStorageSpot[]
  path: string
}

/**
 * Structured data for the store list
 * @see https://developers.google.com/search/docs/appearance/structured-data/carousel
 * @see https://developers.google.com/search/docs/appearance/structured-data/local-business
 */
export const StoreItemListStructuredData = ({ path, stores }: StoreItemListStructuredDataProps) => {
  const { getUrl } = useUrl()
  const { t } = useTranslation()
  const { language } = useLocale()
  const formatRating = useFormatDecimals({ maxDecimalPlaces: 2 })

  const getListItemUrl = (store: NearbyStorageSpot) => {
    // Make sure we don't add links to the non indexed customer pages
    if (!store.marketingPageEnabled) {
      return `${path}#${getStoreAnchor(store.id)}`
    }

    return getUrl({ path: getStoreUrlPath({ store }), absolute: true, language })
  }

  return (
    <JsonLd<ItemList>
      item={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: stores
          .filter(store => store.marketingPageEnabled)
          .map((store: NearbyStorageSpot, position) => {
            const url = getListItemUrl(store)

            return {
              "@type": "ListItem",
              position: position + 1,
              item: {
                "@type": "LocalBusiness",
                "@id": url,
                address: {
                  "@type": "PostalAddress",
                  addressLocality: store.city.name,
                  addressCountry: store.city.country,
                  ...("gmbAddress" in store && store.gmbAddress
                    ? {
                        streetAddress: store.gmbAddress.addressLines,
                        postalCode: store.gmbAddress.postalCode,
                      }
                    : {}),
                },
                aggregateRating: getAggregateRatingSpecification({
                  count: store.reviewCount,
                  value: formatRating(store.rating ?? 0),
                }),
                priceRange: getCurrencySymbol(store.city.currency),
                geo: getGeoCoordinatesSpecification({
                  latitude: store.coordinates.latitude,
                  longitude: store.coordinates.longitude,
                }),
                name: t("cmp.storeItemListStructuredData.nameV4", "Luggage storage {{nickname}}", {
                  nickname: store.nickname,
                }),
                url,
                image: getStoreThumbnail(store),
              },
            }
          }),
      }}
    />
  )
}
